/**
 * Tabs ("views") of RingilDataTable v3 — a named, saved set of filters, sort and columns.
 *
 * Plain JSON in the shape of the backend DTOs (`/api/v1.0/table-views`). Where it lives is the
 * {@link TableViewStore}'s business — the API, or localStorage until the backend is deployed.
 */

/** Stable key of a field in a table's catalogue (e.g. `LOADING_DATE`); persisted, so never renamed. */
export type FieldKey = string;

export type ViewVisibility = 'PRIVATE' | 'COMPANY';

export interface ViewSort {
  sortField: string;
  sortDirection: 'ASC' | 'DESC';
}

/**
 * One table column: an ordered stack of fields, rendered top to bottom in one cell. One member is an
 * ordinary column. A field sits in at most one column of a view, so no value shows twice in a row.
 */
export interface ViewColumn {
  /** Stable across reorders, so React keys and drop targets don't shift under the user. */
  id: string;
  /** Never empty — an emptied column is dropped. */
  members: FieldKey[];
  /**
   * The user's own heading. Absent = derived from the members (the preset's name, or "Carrier / Total
   * price"). Free text, so it is shown as typed, never translated.
   */
  title?: string;
  /** The user's own width in px, set by dragging the header edge. Absent = derived from the members. */
  width?: number;
}

/** What a tab holds — and what a working copy changes until the user saves or discards it. */
export interface ViewContent {
  filter: Record<string, unknown>;
  sort: ViewSort;
  columns: ViewColumn[];
  /**
   * Filter keys shown in the chip bar even when empty ("hearted"). `undefined` = the table's defaults;
   * an empty array is a deliberate "only active filters".
   */
  favouriteFilters?: string[];
  /** Rows per page. `undefined` = {@link DEFAULT_PAGE_SIZE}. */
  pageSize?: number;
  /**
   * `scroll`: rows load as the user scrolls, instead of pages — only where the table offers it.
   * `undefined` = pages.
   */
  pagingMode?: 'pages' | 'scroll';
}

/** Rows per page of a tab that never had its page size changed. */
export const DEFAULT_PAGE_SIZE = 20;

export interface TableView extends ViewContent {
  id: string;
  /** Which table the view belongs to (e.g. `expedition_list`). */
  tableKey: string;
  name: string;
  ownerId: string;
  /** Who shared it, for a colleague's tab. Only the API store knows it. */
  ownerFullName?: string;
  /**
   * The backend's say on whether this user may save, rename, share or delete it — the owner, unless
   * that account was deactivated. Absent (localStorage) = decided by `ownerId`.
   */
  editable?: boolean;
  visibility: ViewVisibility;
  createdAt: string;
  updatedAt: string;
  /** Set by the store on every save; sent back unchanged so a concurrent edit is detected. Never computed. */
  version: number;
}

/** Per-user, per-table preferences: the order of the user's tabs (shared ones included) and the hidden ones. */
export interface TableViewPrefs {
  tableKey: string;
  order: string[];
  /** Shared views the user hid. Their own views are deleted, never hidden. */
  hidden: string[];
  activeViewId?: string;
  /**
   * The user's own pages-or-scroll on colleagues' tabs, by view id: a shared tab opens in its
   * owner's mode until the viewer picks another, which stays theirs. Own tabs keep it in the tab.
   */
  pagingModes?: Record<string, 'pages' | 'scroll'>;
  /** The same for column widths: the user's own px, by view id and then column id, over the owner's. */
  columnWidths?: Record<string, Record<string, number>>;
}
